import { useState, useEffect } from 'react';
import { documentsApi, collaborateursApi, statsApi } from '../api';
import type { Document, CollaborateurWithStats, DashboardStats } from '../types';
import { useSettings } from '../context/SettingsContext';
import { useSidebarResize } from '../hooks/useSidebarResize';
import {
  Users, FileText, AlertTriangle, RefreshCw, ChevronRight, X, GitMerge, ArrowLeft, Folder,
} from 'lucide-react';
import { DocumentDetailPanel } from '../components/DocumentDetailPanel';
import {
  Header, SearchBar, StatsCard, Initials,
  TYPE_COLORS, STATUS_CONFIG,
} from '../components/ui';
import { timeAgo } from '../utils';

export function CollaborateursPage() {
  const { formatDate } = useSettings();
  const [collaborateurs, setCollaborateurs] = useState<CollaborateurWithStats[]>([]);
  const [dashStats, setDashStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCollabs, setTotalCollabs] = useState(0);
  const PAGE_SIZE = 20;
  const [selectedCollab, setSelectedCollab] = useState<CollaborateurWithStats | null>(null);
  const [selectedCollabDocs, setSelectedCollabDocs] = useState<Document[]>([]);
  const [loadingCollabPanel, setLoadingCollabPanel] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeSourceId, setMergeSourceId] = useState('');
  const [merging, setMerging] = useState(false);
  // Liste exhaustive utilisée uniquement pour le sélecteur de fusion (chargée à la demande).
  const [mergeCandidates, setMergeCandidates] = useState<CollaborateurWithStats[]>([]);
  const [loadingMergeCandidates, setLoadingMergeCandidates] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  // Code du type de document actuellement ouvert (null = vue "dossiers")
  const [openFolder, setOpenFolder] = useState<string | null>(null);
  const [editingDateSortie, setEditingDateSortie] = useState(false);
  const [dateSortieInput, setDateSortieInput] = useState('');
  const [savingDateSortie, setSavingDateSortie] = useState(false);
  const { width: sidebarWidth, handleMouseDown: handleSidebarResize, isResizing } = useSidebarResize('collabs-sidebar-width')

  const loadCollabs = () => {
    setLoading(true);
    const params: Record<string, any> = { page: currentPage, size: PAGE_SIZE };
    if (search) params.search = search;
    collaborateursApi.list(params)
      .then(r => {
        setCollaborateurs(r.data.items as CollaborateurWithStats[]);
        setTotalPages(r.data.pages);
        setTotalCollabs(r.data.total);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    statsApi.dashboard().then(r => setDashStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadCollabs(); }, [search, currentPage]);

  // Recherche → revient à la première page (évite d'être bloqué sur une page vide).
  useEffect(() => { setCurrentPage(1); }, [search]);

  const handleSaveDateSortie = () => {
    if (!selectedCollab) return;
    setSavingDateSortie(true);
    const payload: Record<string, string | null> = {
      date_sortie: dateSortieInput ? new Date(dateSortieInput).toISOString() : null,
    };
    collaborateursApi.update(selectedCollab.id, payload)
      .then(() => {
        setEditingDateSortie(false);
        loadCollabs();
        handleSelectCollab({ ...selectedCollab, date_sortie: dateSortieInput || undefined });
      })
      .finally(() => setSavingDateSortie(false));
  };

  const handleSelectCollab = (c: CollaborateurWithStats) => {
    setSelectedCollab(c);
    setSelectedCollabDocs([]);
    setShowMergeModal(false);
    setMergeSourceId('');
    setSelectedDocId(null);
    setOpenFolder(null);
    setEditingDateSortie(false);
    setDateSortieInput(c.date_sortie ? c.date_sortie.slice(0, 10) : '');
    setLoadingCollabPanel(true);
    documentsApi.list({ collaborateur_id: c.id, size: 50 })
      .then(r => setSelectedCollabDocs(r.data.items))
      .finally(() => setLoadingCollabPanel(false));
  };

  const handleMerge = () => {
    if (!selectedCollab || !mergeSourceId) return;
    setMerging(true);
    collaborateursApi.merge(selectedCollab.id, mergeSourceId)
      .then(() => {
        setShowMergeModal(false);
        setMergeSourceId('');
        handleSelectCollab(selectedCollab);
        loadCollabs();
      })
      .finally(() => setMerging(false));
  };

  // Charge la liste exhaustive des collaborateurs pour le sélecteur de fusion.
  // Indépendant de la pagination : on ne veut pas être limité aux 20 visibles.
  const handleOpenMergeModal = () => {
    setShowMergeModal(true);
    setLoadingMergeCandidates(true);
    collaborateursApi.list({ size: 1000 })
      .then(r => setMergeCandidates(r.data.items as CollaborateurWithStats[]))
      .catch(() => setMergeCandidates([]))
      .finally(() => setLoadingMergeCandidates(false));
  };

  const otherCollabs = mergeCandidates.filter(c => c.id !== selectedCollab?.id);

  return (
    <div className="flex flex-1 overflow-hidden">
      <div className="flex-1 overflow-y-auto">
        <Header title=" Collaborateurs">
          <SearchBar placeholder="Rechercher un collaborateur..." value={search} onChange={setSearch} />
        </Header>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <StatsCard label="Total collaborateurs" value={dashStats?.total_collaborateurs ?? '—'} icon={Users} iconBg="bg-blue-50" iconColor="text-blue-600" />
            <StatsCard label="Alertes expiration" value={dashStats?.active_alerts ?? '—'} icon={AlertTriangle} iconBg="bg-red-50" iconColor="text-red-600" accent="text-red-600" />
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">
              {totalCollabs} collaborateur{totalCollabs !== 1 ? 's' : ''}
              {totalPages > 1 && (
                <span className="text-gray-400"> · page {currentPage}/{totalPages}</span>
              )}
            </span>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <RefreshCw className="w-8 h-8 text-gray-300 animate-spin" />
            </div>
          ) : collaborateurs.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 rounded-xl border border-dashed border-gray-200">
              <Users className="w-12 h-12 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-500 font-semibold">Aucun collaborateur</p>
            </div>
          ) : (
            <div className="space-y-3">
              {collaborateurs.map((c) => {
                const isSelected = selectedCollab?.id === c.id;
                return (
                  <div
                    key={c.id}
                    className={`bg-white rounded-xl border shadow-sm overflow-hidden transition cursor-pointer hover:shadow-md ${isSelected ? 'border-teal-300 ring-1 ring-teal-200' : 'border-gray-100'}`}
                    onClick={() => handleSelectCollab(c)}
                  >
                    <div className="flex items-center justify-between p-5">
                      <div className="flex items-center gap-4">
                        <Initials nom_complet={c.nom_complet} size="lg" />
                        <p className="font-bold text-gray-900 uppercase">{c.nom_complet}</p>
                      </div>
                      <div className="flex items-center gap-6">
                        <span className="text-sm text-gray-500">{c.document_count ?? 0} document{(c.document_count ?? 0) !== 1 ? 's' : ''}</span>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1 py-4">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
              >
                ←
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
                  if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('ellipsis');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) =>
                  p === 'ellipsis' ? (
                    <span key={`e-${idx}`} className="px-2 text-gray-400 text-sm">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p as number)}
                      className={`w-8 h-8 text-sm rounded-lg border transition ${
                        currentPage === p
                          ? 'bg-teal-600 text-white border-teal-600 font-semibold'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
              >
                →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Backdrop mobile panneau détail */}
      {(selectedCollab || loadingCollabPanel) && (
        <div
          className="fixed inset-0 z-30 bg-black/30 lg:hidden"
          onClick={() => { setSelectedCollab(null); setSelectedCollabDocs([]); setShowMergeModal(false); }}
        />
      )}

      {/* Handle resize — élément flex entre la liste et la sidebar, lg uniquement */}
      {(selectedCollab || loadingCollabPanel) && (
        <div
          onMouseDown={handleSidebarResize}
          className="hidden lg:flex w-2 items-center justify-center cursor-col-resize group flex-shrink-0 bg-transparent hover:bg-teal-50 transition-colors"
          title="Redimensionner le panneau"
        >
          <div className={`w-0.5 h-16 rounded-full transition-colors ${isResizing ? 'bg-teal-500' : 'bg-gray-200 group-hover:bg-teal-400'}`} />
        </div>
      )}

      {/* Detail Panel */}
      {(selectedCollab || loadingCollabPanel) && (
        <div
          className="fixed inset-y-0 right-0 z-40 w-[92vw] sm:w-96 bg-white border-l border-gray-200 overflow-y-auto lg:static lg:inset-auto lg:shrink-0"
          style={{ width: sidebarWidth }}
        >
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            {selectedDocId ? (
              <button
                onClick={() => setSelectedDocId(null)}
                className="flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900 transition"
              >
                <ArrowLeft className="w-4 h-4" /> {openFolder ? 'Retour au dossier' : 'Retour au collaborateur'}
              </button>
            ) : openFolder ? (
              <button
                onClick={() => setOpenFolder(null)}
                className="flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900 transition"
              >
                <ArrowLeft className="w-4 h-4" /> Retour aux dossiers
              </button>
            ) : (
              <h3 className="font-bold text-gray-900">Détail collaborateur</h3>
            )}
            <button onClick={() => { setSelectedDocId(null); setOpenFolder(null); setSelectedCollab(null); setSelectedCollabDocs([]); setShowMergeModal(false); }} className="p-1 hover:bg-gray-100 rounded-lg">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {selectedDocId ? (
            <DocumentDetailPanel
              docId={selectedDocId}
              onClose={() => setSelectedDocId(null)}
              onUpdated={() => {
                loadCollabs();
                if (selectedCollab) {
                  setLoadingCollabPanel(true);
                  documentsApi.list({ collaborateur_id: selectedCollab.id, size: 50 })
                    .then(r => setSelectedCollabDocs(r.data.items))
                    .finally(() => setLoadingCollabPanel(false));
                }
              }}
            />
          ) : loadingCollabPanel ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : selectedCollab && (
            <>
              <div className="p-5 flex flex-col items-center text-center border-b border-gray-100">
                <Initials nom_complet={selectedCollab.nom_complet} size="lg" />
                <p className="mt-3 font-bold text-gray-900 text-lg uppercase">{selectedCollab.nom_complet}</p>
                {selectedCollab.departement && <p className="text-xs text-teal-600 font-medium mt-0.5">{selectedCollab.departement}</p>}
              </div>

              <div className="p-5 border-b border-gray-100 space-y-2.5">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Informations</h4>
                {selectedCollab.date_entree && (
                  <div className="flex justify-between items-center py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">Date d'entrée</span>
                    <span className="text-sm font-semibold text-gray-900">{formatDate(selectedCollab.date_entree)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-1.5 border-b border-gray-50">
                  <span className="text-sm text-gray-500">Date de sortie</span>
                  {editingDateSortie ? (
                    <div className="flex items-center gap-1.5">
                      <input
                        type="date"
                        value={dateSortieInput}
                        onChange={e => setDateSortieInput(e.target.value)}
                        className="border border-gray-200 rounded px-2 py-0.5 text-xs"
                      />
                      <button
                        onClick={handleSaveDateSortie}
                        disabled={savingDateSortie}
                        className="text-xs bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white px-2 py-0.5 rounded font-medium"
                      >
                        {savingDateSortie ? '...' : 'OK'}
                      </button>
                      <button
                        onClick={() => setEditingDateSortie(false)}
                        className="text-xs text-gray-400 hover:text-gray-600 px-1"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setEditingDateSortie(true)}
                      className="text-sm font-semibold text-gray-900 hover:text-teal-600 transition text-right"
                    >
                      {selectedCollab.date_sortie ? formatDate(selectedCollab.date_sortie) : <span className="text-gray-400 text-xs">— Définir</span>}
                    </button>
                  )}
                </div>
                <div className="flex justify-between items-center py-1.5 border-b border-gray-50">
                  <span className="text-sm text-gray-500">Documents</span>
                  <span className="text-sm font-semibold text-gray-900">{selectedCollab.document_count ?? 0}</span>
                </div>
                {(selectedCollab.expiring_alerts ?? 0) > 0 && (
                  <div className="flex justify-between items-center py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">Alertes expiration</span>
                    <span className="text-sm font-semibold text-orange-600">{selectedCollab.expiring_alerts}</span>
                  </div>
                )}
              </div>

              {/* Fusion collaborateur */}
              <div className="px-5 py-4 border-b border-gray-100">
                {!showMergeModal ? (
                  <button
                    onClick={handleOpenMergeModal}
                    className="w-full flex items-center justify-center gap-2 border border-orange-200 text-orange-600 hover:bg-orange-50 px-4 py-2 rounded-lg text-sm font-medium transition"
                  >
                    <GitMerge className="w-4 h-4" /> Fusionner avec un doublon
                  </button>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs text-gray-500 font-medium">
                      Tous les documents du collaborateur sélectionné seront transférés vers <span className="font-bold text-gray-800">{selectedCollab.nom_complet}</span>, puis le doublon sera supprimé.
                    </p>
                    <select
                      value={mergeSourceId}
                      onChange={e => setMergeSourceId(e.target.value)}
                      disabled={loadingMergeCandidates}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-400"
                    >
                      <option value="">
                        {loadingMergeCandidates ? 'Chargement…' : '— Choisir le doublon à fusionner —'}
                      </option>
                      {otherCollabs.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.nom_complet} ({c.document_count ?? 0} doc{(c.document_count ?? 0) !== 1 ? 's' : ''})
                        </option>
                      ))}
                    </select>
                    <div className="flex gap-2">
                      <button
                        onClick={handleMerge}
                        disabled={!mergeSourceId || merging}
                        className="flex-1 flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white px-4 py-2 rounded-lg text-sm font-semibold transition"
                      >
                        {merging ? <RefreshCw className="w-4 h-4 animate-spin" /> : <GitMerge className="w-4 h-4" />}
                        {merging ? 'Fusion...' : 'Confirmer la fusion'}
                      </button>
                      <button
                        onClick={() => { setShowMergeModal(false); setMergeSourceId(''); }}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                      >
                        <X className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-5">
                {(() => {
                  // Groupement des documents par type (code → { label, docs })
                  const folders = selectedCollabDocs.reduce<Record<string, { label: string; docs: Document[] }>>((acc, doc) => {
                    const code = doc.document_type?.code ?? 'autre';
                    const label = doc.document_type?.label ?? 'Autre';
                    if (!acc[code]) acc[code] = { label, docs: [] };
                    acc[code].docs.push(doc);
                    return acc;
                  }, {});
                  const folderEntries = Object.entries(folders).sort(([, a], [, b]) => a.label.localeCompare(b.label));
                  const currentFolder = openFolder ? folders[openFolder] : null;

                  if (selectedCollabDocs.length === 0) {
                    return (
                      <>
                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Documents</h4>
                        <p className="text-sm text-gray-400 text-center py-4">Aucun document</p>
                      </>
                    );
                  }

                  if (currentFolder) {
                    return (
                      <>
                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                          {currentFolder.label} · {currentFolder.docs.length} document{currentFolder.docs.length !== 1 ? 's' : ''}
                        </h4>
                        <div className="space-y-2">
                          {currentFolder.docs.map((doc) => {
                            const tc = TYPE_COLORS[openFolder!] || TYPE_COLORS.autre;
                            const sc = STATUS_CONFIG[doc.status] || STATUS_CONFIG.en_cours;
                            return (
                              <div
                                key={doc.id}
                                className="bg-gray-50 rounded-lg border border-gray-100 overflow-hidden cursor-pointer hover:border-teal-200 hover:shadow-sm transition"
                                onClick={() => setSelectedDocId(doc.id)}
                              >
                                <div className={`h-8 ${tc.bg} flex items-center px-3 gap-2`}>
                                  <FileText className={`w-3.5 h-3.5 ${tc.text} opacity-60`} />
                                  <span className={`text-xs font-bold ${tc.text} uppercase tracking-wide truncate flex-1`}>{currentFolder.label}</span>
                                  <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                </div>
                                <div className="px-3 py-2">
                                  <p className="text-xs font-semibold text-gray-800 truncate">{doc.original_filename}</p>
                                  <div className="flex items-center justify-between mt-1.5">
                                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold ${sc.color}`}>{sc.label}</span>
                                    <span className="text-xs text-gray-400">{timeAgo(doc.created_at)}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    );
                  }

                  return (
                    <>
                      <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                        Dossiers · {folderEntries.length} type{folderEntries.length !== 1 ? 's' : ''}
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        {folderEntries.map(([code, folder]) => {
                          const tc = TYPE_COLORS[code] || TYPE_COLORS.autre;
                          return (
                            <button
                              key={code}
                              onClick={() => setOpenFolder(code)}
                              className={`${tc.bg} rounded-lg border border-gray-100 hover:border-teal-200 hover:shadow-sm transition p-3 text-left flex flex-col items-start gap-2`}
                            >
                              <Folder className={`w-6 h-6 ${tc.text}`} />
                              <span className={`text-xs font-bold ${tc.text} uppercase tracking-wide line-clamp-2`}>{folder.label}</span>
                              <span className="text-xs text-gray-500">{folder.docs.length} document{folder.docs.length !== 1 ? 's' : ''}</span>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
